// The peaks behind the scrubbable waveform.
//
// A waveform is only worth drawing if it is *this* clip's waveform. A synthetic
// row of bars that ignores the audio looks like information and is not, and on
// a lesson slide that is worse than an honest plain bar — so the peaks here are
// measured from the real samples, and when they cannot be measured the player
// falls back to a flat timeline rather than inventing a shape.
//
// Measuring them means reading the bytes, and reading the bytes is the one part
// of audio that *does* need the host's permission: playback works cross-origin
// without a CORS grant, `fetch` does not. So this can fail on a host that plays
// the clip perfectly well, and the failure is expected rather than exceptional.
// The caller is expected to have a fallback.

import { toFetchableUrl } from './assetUrls.js';

/** Bars in a drawn waveform. Enough to show shape at slide width, small enough
 *  to keep in a deck's worth of cached entries without thinking about it. */
export const DEFAULT_BUCKETS = 320;

/** How long one clip gets before the drawing is given up on. */
export const WAVEFORM_FETCH_TIMEOUT_MS = 30000;

/**
 * The largest clip worth drawing.
 *
 * Decoding expands compressed audio to 32-bit float PCM — roughly ten times the
 * file — and holds all of it at once. A 20 MB recording is already ~200 MB of
 * samples for a picture 320 bars wide, and an hour-long lecture capture would
 * take the tab down. Past this the flat track takes over, which scrubs exactly
 * the same; only the drawing is lost.
 */
export const MAX_WAVEFORM_BYTES = 20 * 1024 * 1024;

// Decoding is the expensive half (a few hundred ms for a minute of audio), and
// the same clip is commonly on several slides — or re-rendered by every edit —
// so the reduced peaks are kept. Only the peaks: the decoded PCM for one minute
// of stereo audio is ~20 MB and is released as soon as it has been reduced.
const CACHE_LIMIT = 8;
const cache = new Map();

const remember = (key, promise) => {
  cache.set(key, promise);
  while (cache.size > CACHE_LIMIT) {
    cache.delete(cache.keys().next().value);
  }
  return promise;
};

const getAudioContextCtor = () =>
  (typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext)) || null;

/**
 * One read. Throws with `canRetry` set for the failures a cache-skipping second
 * attempt fixes.
 */
const readOnce = async (doFetch, url, cache) => {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), WAVEFORM_FETCH_TIMEOUT_MS)
    : null;
  try {
    const init = { mode: 'cors', cache };
    if (controller) init.signal = controller.signal;
    const response = await doFetch(url, init);
    if (!response.ok) {
      const error = new Error(`the host answered ${response.status}`);
      error.canRetry = true;
      throw error;
    }
    const declared = Number(response.headers?.get?.('content-length'));
    // A fact about the file, not about the cache, so it is not retried.
    if (Number.isFinite(declared) && declared > MAX_WAVEFORM_BYTES) {
      throw new Error('that clip is too long to draw');
    }
    const bytes = await response.arrayBuffer();
    if (bytes.byteLength > MAX_WAVEFORM_BYTES) throw new Error('that clip is too long to draw');
    return bytes;
  } catch (error) {
    // A refused read arrives as a bare TypeError with no status to inspect.
    if (error instanceof TypeError) error.canRetry = true;
    throw error;
  } finally {
    if (timer) clearTimeout(timer);
  }
};

/**
 * Read the clip's bytes, once the ordinary way and once stepping over the
 * browser's cache.
 *
 * The retry is not superstition — it is the same lesson remoteImage.js learned,
 * and audio walks into it every single time. Playing a clip fills the browser's
 * cache from a media request, which carries no `Origin`; the asset host answers
 * that (correctly) with no `Access-Control-Allow-Origin`, and `Vary: Origin`
 * cannot save a cache entry that was already stored without one. So the very
 * act of checking that a pasted link plays is what poisons the entry this read
 * then finds, and the waveform disappeared for a clip whose host allows this
 * app perfectly well. `cache: 'reload'` asks the host again, with an Origin.
 */
const readBytes = async (doFetch, url) => {
  let firstError = null;
  for (const cache of ['default', 'reload']) {
    try {
      return await readOnce(doFetch, url, cache);
    } catch (error) {
      firstError = firstError || error;
      if (!error.canRetry) break;
    }
  }
  throw firstError;
};

/**
 * Reduce decoded samples to one 0..1 height per bar.
 *
 * Peak rather than RMS: speech is mostly quiet with short loud syllables, and
 * an RMS reduction of a spoken clip draws a nearly flat line. The peak of each
 * bucket is what makes a voice recording look like a voice recording.
 *
 * Channels are folded together by taking the larger absolute sample, so a clip
 * mixed hard to one side does not draw half a waveform.
 */
export const peaksFromBuffer = (audioBuffer, buckets = DEFAULT_BUCKETS) => {
  const channels = [];
  for (let c = 0; c < audioBuffer.numberOfChannels; c += 1) {
    channels.push(audioBuffer.getChannelData(c));
  }
  if (!channels.length) return [];

  const total = channels[0].length;
  const size = Math.max(1, Math.floor(total / buckets));
  const peaks = new Array(buckets).fill(0);
  let loudest = 0;

  for (let i = 0; i < buckets; i += 1) {
    const start = i * size;
    const end = Math.min(total, start + size);
    let peak = 0;
    for (let j = start; j < end; j += 1) {
      for (let c = 0; c < channels.length; c += 1) {
        const value = Math.abs(channels[c][j]);
        if (value > peak) peak = value;
      }
    }
    peaks[i] = peak;
    if (peak > loudest) loudest = peak;
  }

  // Normalised against the clip's own loudest moment: a quietly recorded
  // acknowledgement should still fill the bar, the same way every audio editor
  // draws it.
  if (loudest > 0) {
    for (let i = 0; i < buckets; i += 1) peaks[i] /= loudest;
  }
  return peaks;
};

const decodeArrayBuffer = (ctx, arrayBuffer) => new Promise((resolve, reject) => {
  // The promise form is not in older Safari, which only takes the callbacks.
  // Calling the callback form covers both — the promise it also returns is
  // simply ignored where it exists.
  try {
    const maybe = ctx.decodeAudioData(arrayBuffer, resolve, reject);
    if (maybe && typeof maybe.then === 'function') maybe.then(resolve, reject);
  } catch (error) {
    reject(error);
  }
});

/**
 * Fetch, decode and reduce a clip to its waveform peaks.
 *
 * @returns {Promise<number[]>} one 0..1 height per bar.
 * @throws when the bytes cannot be read (most often the host's CORS rules) or
 *         cannot be decoded. Both are ordinary outcomes here; the player draws
 *         a plain timeline instead.
 */
export const loadWaveformPeaks = (src, { buckets = DEFAULT_BUCKETS, fetchImpl } = {}) => {
  if (!src) return Promise.reject(new Error('no clip to read'));
  const key = `${buckets}:${src}`;
  if (cache.has(key)) return cache.get(key);

  const run = (async () => {
    const AudioContextCtor = getAudioContextCtor();
    if (!AudioContextCtor) throw new Error('this browser has no Web Audio support');

    const doFetch = fetchImpl || (typeof window !== 'undefined' && typeof window.fetch === 'function'
      ? window.fetch.bind(window)
      : null);
    if (!doFetch) throw new Error('this browser cannot read the file');

    const arrayBuffer = await readBytes(doFetch, toFetchableUrl(src));

    // Browsers cap how many audio contexts a page may hold open, so this one is
    // built for the decode and closed straight after it.
    const ctx = new AudioContextCtor();
    try {
      const decoded = await decodeArrayBuffer(ctx, arrayBuffer);
      return peaksFromBuffer(decoded, buckets);
    } finally {
      if (typeof ctx.close === 'function') ctx.close().catch(() => {});
    }
  })();

  // A failed read is not worth caching — the host's CORS rules may be the very
  // thing the author is about to fix.
  run.catch(() => cache.delete(key));
  return remember(key, run);
};

/** Test seam: forget everything measured so far. */
export const clearWaveformCache = () => cache.clear();

